import { useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import { Switch, Typography } from 'antd';
import {
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import GradientAvatar from '../shared/GradientAvatar';
import type { Provider } from '../../types/business';

const { Text } = Typography;

interface Props {
  provider: Provider;
  busy: boolean;
  onToggle: (provider: Provider) => void;
  onBlockToggle: (provider: Provider) => void;
  onDelete: (provider: Provider) => void;
}

interface IconButtonProps {
  icon: ReactNode;
  color: string;
  bg: string;
  border: string;
  ariaLabel: string;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
}

function IconActionButton({
  icon,
  color,
  bg,
  border,
  ariaLabel,
  disabled,
  loading,
  onClick,
}: IconButtonProps) {
  const [pressed, setPressed] = useState(false);
  const inactive = disabled || loading;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (!inactive) onClick();
      }}
      disabled={inactive}
      aria-label={ariaLabel}
      onTouchStart={() => !inactive && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => !inactive && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        width: 32,
        height: 32,
        borderRadius: 10,
        background: bg,
        border: `1px solid ${border}`,
        color,
        fontSize: 13,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: inactive ? 'not-allowed' : 'pointer',
        transform: pressed && !inactive ? 'scale(0.92)' : 'scale(1)',
        transition: 'transform 0.1s ease, background 0.15s ease',
        opacity: inactive ? 0.55 : 1,
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        flexShrink: 0,
      }}
    >
      {loading ? <LoadingOutlined spin /> : icon}
    </button>
  );
}

export default function ProviderRow({
  provider,
  busy,
  onToggle,
  onBlockToggle,
  onDelete,
}: Props) {
  const { t } = useTranslation('owner');
  const isActive = provider.active;
  const isBlocked = !provider.enabledByOwner;

  // Status colors for avatar dot indicator
  const statusColor = isBlocked
    ? '#ef4444'
    : isActive
      ? '#1f8b4c'
      : '#cbd5e1';

  // Switch propagation stoppers so it doesn't bubble to row
  const stop = {
    onClick: (e: SyntheticEvent) => e.stopPropagation(),
    onKeyDown: (e: SyntheticEvent) => e.stopPropagation(),
    onMouseDown: (e: SyntheticEvent) => e.stopPropagation(),
    onTouchStart: (e: SyntheticEvent) => e.stopPropagation(),
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: 'var(--bl-surface, #fff)',
        border: '1px solid var(--bl-divider, #ebebeb)',
        borderRadius: 14,
        boxShadow: 'var(--bl-shadow-soft, 0 2px 10px rgba(0,0,0,0.04))',
        opacity: isBlocked ? 0.72 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      <GradientAvatar
        id={provider.id}
        initials={provider.displayName[0] ?? ''}
        size={42}
        statusColor={statusColor}
        dimmed={isBlocked}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 700,
            color: isBlocked
              ? 'var(--bl-text-muted, #6b7280)'
              : 'var(--bl-text-strong, #0f172a)',
            letterSpacing: -0.1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: 4,
          }}
        >
          {provider.displayName}
        </Text>

        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          {/* Active / Inactive pill */}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 10,
              fontWeight: 700,
              color: isActive ? '#1f8b4c' : 'var(--bl-text-muted, #6b7280)',
              background: isActive
                ? 'rgba(31,139,76,0.10)'
                : 'var(--bl-muted-soft, #eef0f3)',
              border: isActive ? '1px solid rgba(31,139,76,0.20)' : '1px solid var(--bl-divider, #e5e7eb)',
              borderRadius: 999,
              padding: '2px 8px',
              letterSpacing: 0.3,
              textTransform: 'uppercase',
            }}
          >
            {isActive ? t('providers.statusActive') : t('providers.statusInactive')}
          </span>

          {/* Blocked badge */}
          {isBlocked && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 10,
                fontWeight: 700,
                color: '#cf1322',
                background: 'rgba(239,68,68,0.10)',
                border: '1px solid rgba(239,68,68,0.22)',
                borderRadius: 999,
                padding: '2px 8px',
                letterSpacing: 0.3,
                textTransform: 'uppercase',
              }}
            >
              <LockOutlined style={{ fontSize: 9 }} />
              {t('providers.blockedBadge')}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <div {...stop}>
          <Switch
            size="small"
            checked={isActive}
            onChange={() => onToggle(provider)}
          />
        </div>

        <IconActionButton
          icon={isBlocked ? <LockOutlined /> : <UnlockOutlined />}
          color={isBlocked ? '#ef4444' : 'var(--bl-text-muted, #6b7280)'}
          bg={isBlocked ? 'rgba(239,68,68,0.08)' : 'var(--bl-surface-soft, #f0f4f8)'}
          border={isBlocked ? 'rgba(239,68,68,0.22)' : 'var(--bl-divider, #e5e7eb)'}
          ariaLabel={isBlocked ? t('providers.unblockConfirm', { name: '' }) : t('providers.blockConfirm', { name: '' })}
          loading={busy}
          onClick={() => onBlockToggle(provider)}
        />

        <IconActionButton
          icon={<DeleteOutlined />}
          color="#ef4444"
          bg="rgba(239,68,68,0.06)"
          border="rgba(239,68,68,0.20)"
          ariaLabel={t('providers.deleteSheetTitle')}
          onClick={() => onDelete(provider)}
        />
      </div>
    </div>
  );
}
